'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { StoreCapabilitiesDto, StoresApi } from '@/lib/Apis';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { getCurrentConfig } from '@/lib/api-adapter/config';

interface UseStoreCapabilitiesReturn {
    capabilities: StoreCapabilitiesDto | null;
    isLoading: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
    // Computed convenience properties
    showCardPayment: boolean;
    showCashPayment: boolean;
    showShippoDelivery: boolean;
    showCustomDelivery: boolean;
}

export function useStoreCapabilities(): UseStoreCapabilitiesReturn {
    const config = getCurrentConfig();
    const storeId = config?.storeId;

    const { data: capabilities, isLoading, error, refetch } = useQuery({
        queryKey: ['storeCapabilities', storeId],
        queryFn: async () => {
            if (!storeId) {
                throw new Error('Store ID not configured');
            }
            const api = new StoresApi(AXIOS_CONFIG);
            const response = await api.getStoreCapabilities(storeId);
            return response.data;
        },
        staleTime: 10 * 60 * 1000, // 10 minutes - store capabilities rarely change
        enabled: !!storeId,
    });

    const refresh = async () => {
        await refetch();
    };

    // Computed convenience properties based on the logic from the API guide
    const showCardPayment = Boolean(capabilities?.hasStripeKey && capabilities?.cardPaymentEnabled);
    const showCashPayment = Boolean(capabilities?.cashPaymentEnabled);
    const showShippoDelivery = Boolean(capabilities?.hasShippoConnected && capabilities?.shippoDeliveryEnabled);
    const showCustomDelivery = Boolean(capabilities?.customDeliveryEnabled);

    return {
        capabilities: capabilities || null,
        isLoading,
        error: error as Error | null,
        refresh,
        showCardPayment,
        showCashPayment,
        showShippoDelivery,
        showCustomDelivery,
    };
}
